import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/components/ui/carousel";
import { useEffect, useState } from "react";
import { ScrollArea } from "~/components/ui/scroll-area";
import { X } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import { OutsideAreaWithImages } from "~/lib/server-functions/get-locations";
import { CheckIcon } from "~/icons/CheckIcon";
import { PartyIcon } from "~/icons/PartyIcon";
import { TheatreSeatingIcon } from "~/icons/TheatreSeatingIcon";
import { ClassroomSeatingIcon } from "~/icons/ClassroomSeatingIcon";
import { BlockSeatingIcon } from "~/icons/BlockSeatingIcon";
import { UFormSeatingIcon } from "~/icons/UFormSeatingIcon";
import { BanquetSeatingIcon } from "~/icons/BanquetSeatingIcon";
import { TableSeatingIcon } from "~/icons/TableSeatingIcon";
import { StandingSeatingIcon } from "~/icons/StandingSeatingIcon";
import { SubSection } from "~/components/ContentElements/SubSection";
import { BoxIcon } from "~/icons/BoxIcon";
import { HorizontalDoubleArrowIcon } from "~/icons/HorizontalDoubleArrowIcon";
import { OpenSquareIcon } from "~/icons/OpenSquareIcon";
import { VerticalDoubleArrowIcon } from "~/icons/VerticalDoubleArrowIcon";
import { Button } from "~/components/ui/button";
import { RichTextRenderer } from "~/components/RichTextRenderer";
import { formatGermanCurrency } from "../ui/price-input";
import {
  IMAGE_SIZES,
  buildImageUrl,
  formatGermanNumberWithoutDecimals,
  convertCmToMeters,
} from "~/lib/utils";

export default function OutsideAreaPopUp({
  open,
  onOpenChange,
  onOpenGallery,
  images,
  sections = [{ id: -1, label: "Alle Bilder" }],
  activeImageId,
  outsideArea,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOpenGallery: (value: boolean) => void;
  images: {
    id: number | null;
    path: string;
    mimetype: string | null;
    alt: string | null;
    size: number | null;
    sort: number | null;
    category: string | null;
    categoryId: number | null;
  }[];
  sections?: { id: number; label: string }[];
  activeImageId?: string;
  outsideArea: OutsideAreaWithImages;
}) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [activeImage, setActiveImage] = useState(
    activeImageId ?? images[0]?.id ?? null,
  );
  const [activeTab, setActiveTab] = useState(sections[0].id);

  // Filter and sort images based on activeTab
  const filteredImages = (() => {
    const filtered =
      activeTab === -1
        ? images
        : images.filter((img) => img.categoryId === activeTab);
    return [...filtered].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
  })();

  // Update active image when images, activeTab, or activeImageId changes
  useEffect(() => {
    const filtered =
      activeTab === -1
        ? images
        : images.filter((img) => img.categoryId === activeTab);
    const sorted = [...filtered].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

    if (sorted.length > 0) {
      setActiveImage(activeImageId ?? sorted[0].id ?? null);
    }
  }, [images, activeTab, activeImageId]);

  const layouts = {
    party: outsideArea.seatingParty || null,
    row: outsideArea.seatingRow || null,
    parliament: outsideArea.seatingParliamentary || null,
    block: outsideArea.seatingBlock || null,
    uform: outsideArea.seatingUShape || null,
    banquet: outsideArea.seatingBanquet || null,
    table: outsideArea.seatingTable || null,
    standing: outsideArea.seatingStanding || null,
  } as const;

  const layoutConfig = [
    { key: "party", label: "Party", icon: <PartyIcon /> },
    { key: "row", label: "Reihe", icon: <TheatreSeatingIcon /> },
    { key: "parliament", label: "Parlament", icon: <ClassroomSeatingIcon /> },
    { key: "block", label: "Block", icon: <BlockSeatingIcon /> },
    { key: "uform", label: "U-Form", icon: <UFormSeatingIcon /> },
    { key: "banquet", label: "Bankett", icon: <BanquetSeatingIcon /> },
    { key: "table", label: "Tisch", icon: <TableSeatingIcon /> },
    { key: "standing", label: "Stehend", icon: <StandingSeatingIcon /> },
  ] as const;

  const handleCategoryChange = (id: number) => {
    setActiveTab(id);
  };

  useEffect(() => {
    if (!carouselApi) return;

    const updateCarouselState = () => {
      if (!filteredImages.length) return;

      setActiveImage(filteredImages[carouselApi.selectedScrollSnap()].id);
    };

    updateCarouselState();

    carouselApi.on("select", updateCarouselState);

    return () => {
      carouselApi.off("select", updateCarouselState);
    };
  }, [carouselApi, filteredImages]);

  useEffect(() => {
    setActiveImage(activeImageId ?? images[0]?.id);
  }, [activeImageId]);

  const areaName = outsideArea.name?.german || outsideArea.name?.english || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDescription className="sr-only">
        Außenbereichdetails
      </DialogDescription>
      <DialogContent
        className="sm:max-w-[80vw] max-h-[90vh] p-0 border-0 rounded-lg"
        overlayIntensity="dark"
        showCloseButton={false}
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="inline-flex h-10 w-10 absolute cursor-pointer top-4 right-4 z-20 items-center justify-center rounded-full bg-dove-gray-darker text-white hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black"
        >
          <X className="h-4 w-4" />
        </button>
        <ScrollArea className="h-[90vh] w-full">
          <div className="relative w-full">
            <Button
              variant="outline"
              className="absolute bottom-4 right-4 z-10 border-white text-white"
              onClick={() => {
                onOpenGallery(true);
              }}
            >
              Alle Bilder
            </Button>

            {/* Slide counter below center over carousel */}
            {filteredImages.length > 0 ? (
              <>
                {filteredImages.length > 1 && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 bottom-6 z-20 px-4 py-1 rounded bg-dove-gray-darker text-white text-base font-medium"
                    style={{ pointerEvents: "none" }}
                  >
                    {(carouselApi?.selectedScrollSnap?.() ?? 0) + 1}/
                    {filteredImages.length}
                  </div>
                )}

                <Carousel
                  setApi={setCarouselApi}
                  opts={{
                    loop: filteredImages.length > 1,
                  }}
                  plugins={[
                    ...(filteredImages.length > 1
                      ? [
                        Autoplay({
                          delay: 10000,
                        }),
                      ]
                      : []),
                  ]}
                >
                  <CarouselContent>
                    {filteredImages.map((img, i) => (
                      <CarouselItem key={img.id ?? i}>
                        <div className="relative h-[450px] lg:h-[900px] flex items-center justify-center">
                          <img
                            src={buildImageUrl(img.path, {
                              size: IMAGE_SIZES.galleryLarge,
                            })}
                            alt={img.alt || ""}
                            className="absolute top-0 left-0 w-full h-full object-cover z-0 rounded-t-lg"
                          />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>

                  {filteredImages.length > 1 && (
                    <>
                      <CarouselPrevious
                        variant="flatWhite"
                        className="rounded shadow-[0_4px_10px_0_rgba(0,0,0,0.25)] absolute left-8 top-1/2 -translate-y-1/2"
                      />
                      <CarouselNext
                        variant="flatWhite"
                        className="rounded shadow-[0_4px_10px_0_rgba(0,0,0,0.25)] absolute right-8 top-1/2 -translate-y-1/2"
                      />
                    </>
                  )}
                </Carousel>
              </>
            ) : null}
          </div>

          <div className="px-6 pb-10 pt-6 layout-content-grid">
            <div className="flex flex-col gap-2">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <DialogTitle className="text-2xl font-semibold">
                    {areaName}
                  </DialogTitle>
                </div>
              </div>

              <div className="flex flex-wrap gap-8 py-4">
                {outsideArea.maxArea ? (
                  <div className="flex flex-col items-center justify-between text-center">
                    <div>
                      <BoxIcon />
                    </div>
                    <div>
                      <div className="text-lg font-semibold">
                        {formatGermanNumberWithoutDecimals(
                          convertCmToMeters(outsideArea.maxArea),
                        )}
                      </div>
                      <div className="text-xs">m² Fläche</div>
                    </div>
                  </div>
                ) : null}

                {outsideArea.maxLength ? (
                  <div className="flex flex-col items-center justify-between text-center">
                    <div className="text-3xl">
                      <HorizontalDoubleArrowIcon />
                    </div>
                    <div>
                      <div className="text-lg font-semibold">
                        {formatGermanNumberWithoutDecimals(
                          convertCmToMeters(outsideArea.maxLength),
                        )}
                      </div>
                      <div className="text-xs">m Länge</div>
                    </div>
                  </div>
                ) : null}
                {outsideArea.maxWidth ? (
                  <div className="flex flex-col items-center justify-between text-center">
                    <div className="text-3xl">
                      <OpenSquareIcon />
                    </div>
                    <div>
                      <div className="text-lg font-semibold">
                        {formatGermanNumberWithoutDecimals(
                          convertCmToMeters(outsideArea.maxWidth),
                        )}
                      </div>
                      <div className="text-xs">m Breite</div>
                    </div>
                  </div>
                ) : null}
                {outsideArea.maxHeight ? (
                  <div className="flex flex-col items-center justify-between text-center">
                    <div className="text-3xl">
                      <VerticalDoubleArrowIcon />
                    </div>
                    <div>
                      <div className="text-lg font-semibold">
                        {formatGermanNumberWithoutDecimals(
                          convertCmToMeters(outsideArea.maxHeight),
                        )}
                      </div>
                      <div className="text-xs">m Höhe</div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <SubSection>
              <RichTextRenderer
                richText={outsideArea.longDescription?.german ?? ""}
              />
            </SubSection>

            <SubSection title="Außenbereichdetails">
              <div className="flex flex-col gap-y-4">
                {outsideArea.separable ? (
                  <div className="flex flex-1 max-w-[150px] items-center justify-between">
                    <span>Bereich abtrennbar</span>
                    <CheckIcon />
                  </div>
                ) : null}
                {outsideArea.covered ? (
                  <div className="flex flex-1 max-w-[150px] items-center justify-between">
                    <span>Überdacht</span>
                    <CheckIcon />
                  </div>
                ) : null}
                {outsideArea.accessible ? (
                  <div className="flex flex-1 max-w-[150px] items-center justify-between">
                    <span>barrierefrei</span>
                    <CheckIcon />
                  </div>
                ) : null}
                {outsideArea.withSeating ? (
                  <div className="flex flex-1 max-w-[150px] items-center justify-between">
                    <span>Mit Bestuhlung</span>
                    <CheckIcon />
                  </div>
                ) : null}
                {outsideArea.withWaterAccess ? (
                  <div className="flex flex-1 max-w-[150px] items-center justify-between">
                    <span>Wasseranschluss</span>
                    <CheckIcon />
                  </div>
                ) : null}
              </div>
            </SubSection>

            <SubSection title="Kapazitäten">
              <div className="flex flex-wrap gap-8 py-4">
                {layoutConfig.map((layout) => {
                  const value =
                    layouts[layout.key as keyof typeof layouts] ?? null;
                  if (!value) return null;

                  return (
                    <div
                      key={layout.key}
                      className="flex flex-col items-center justify-center text-center [&_svg]:size-14"
                    >
                      <div className="mb-4">{layout.label}</div>
                      <div className="mb-4">{layout.icon}</div>
                      <div className="text-lg">{value}</div>
                    </div>
                  );
                })}
              </div>
            </SubSection>

            <div className="flex flex-col gap-4 md:flex-row md:gap-12">
              <SubSection
                title="Mietkonditionen"
                className="md:min-w-0 md:flex-1"
              >
                {outsideArea?.priceIsPublic === true ? (
                  (() => {
                    const items = [
                      {
                        label: "Basismietpreis",
                        value: outsideArea?.rateDaily,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} € / Tag`,
                      },
                      {
                        label: "Stundenlimit",
                        value: outsideArea?.rateDailyHourLimit,
                        format: (v: number) =>
                          v === 0 ? "Unbeschränkt" : `${formatGermanNumberWithoutDecimals(v)} Std.`,
                      },
                      {
                        label: "Stundenpreis",
                        value: outsideArea?.rateHourly,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} € / Stunde`,
                      },
                      {
                        label: "Mindeststunden",
                        value: outsideArea?.rateHourlyMinimumHours,
                        format: (v: number) =>
                          `${formatGermanNumberWithoutDecimals(v)} Std.`,
                      },
                      {
                        label: "Pauschale pro Person",
                        value: outsideArea?.consumptionFlatRatePerPerson,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} €`,
                      },
                      {
                        label: "Pauschale Mindestpersonen",
                        value: outsideArea?.consumptionFlatRateMinimumPersons,
                        format: (v: number) =>
                          `${formatGermanNumberWithoutDecimals(v)} Personen`,
                      },
                    ].filter((item) => item.value != null && item.value > 0);

                    if (items.length === 0) return null;

                    return (
                      <div className="space-y-2">
                        {items.map((item) => (
                          <div
                            key={item.label}
                            className="flex justify-between"
                          >
                            <strong>{item.label}</strong>
                            <span>{item.format(item.value!)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                ) : (
                  <div className="flex flex-1 max-w-[250px] items-center justify-between">
                    <strong>Nur individuell auf Anfrage:</strong>
                    <CheckIcon />
                  </div>
                )}
                {outsideArea.additionalPricingInformation?.german ? (
                  <div>
                    <strong>Sonstiges Preisinformationen</strong>
                    <RichTextRenderer
                      richText={
                        outsideArea.additionalPricingInformation?.german ?? ""
                      }
                    />
                  </div>
                ) : null}
              </SubSection>

              <SubSection title="Verzehr" className="md:min-w-0 md:flex-1">
                {outsideArea?.priceIsPublic === true ? (
                  <div className="space-y-2">
                    {[
                      {
                        label: outsideArea?.minimumConsumptionPersons === -1 ? "Mindestverzehr Pauschale" : "Mindestverzehr pro Person",
                        value: outsideArea?.minimumConsumptionPrice,
                        format: (v: number) =>
                          `${formatGermanCurrency(v / 100)} €`,
                      },
                      {
                        label: "Mindestverzehr minimale Personenanzahl",
                        value: outsideArea?.minimumConsumptionPersons,
                        format: (v: number) => `${v} Personen`,
                      },
                    ]
                      .filter((item) => item.value != null && item.value > 0)
                      .map((item) => (
                        <div key={item.label} className="flex justify-between">
                          <strong>{item.label}</strong>
                          <span>{item.format(item.value!)}</span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="flex flex-1 max-w-[250px] items-center justify-between">
                    <strong>Nur individuell auf Anfrage:</strong>
                    <CheckIcon />
                  </div>
                )}
              </SubSection>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
